import { FormApi } from 'final-form'
import { MountingInputsIntegrationData } from 'projectSections/sections/design/systems/tabs/mounting/nativeMounting/types'
import { StudioSystemType } from './studio/items'

export type NativeMountingID =
  | 'sunmodoSMRPitched'
  | 'sunmodoNanoRack'
  | 'solarRoofPro'
  | 'solarNestRooftop'
  | 'solarNestGroundMount'
  | 'variosole'
  | 'schletter'
  | 'fastensol'
  | 'msPitchedProLine'
  | 'msTrapezeProLine'
  | 'esdec'
  | 'flatFix'
  | 'metasole'
  | 'gseInRoof'
  | 'k2OnRoof'
  | 'k2SimpleTilt'
  | 'k2GroundMount'
  | 'k2OnRoofUK'
  | 'solarPortGroundMount'
  | 'nova'
  | 'vanDerValk'
  | 'fastRack'
  | 'snapnrackUltraRail'

export type PartnerAccessoriesID = 'solaskirt'
export type MountingID = 'ironridge' | 'renusol' | 'viridian' | 'gseInRoof' | 'unirac' | NativeMountingID

export type MountingSystemType = 'onRoof' | 'inRoof' | 'flatRoof' | 'ground'

export interface IntegrationData extends MountingInputsIntegrationData {
  enabled?: boolean
}

export type mountingSystemDisplayInfo = {
  displayName: string
  imageFile: string
  id: MountingID | PartnerAccessoriesID
  automated?: boolean
}

export type MountingSystemTypeOptions = { type: MountingSystemType; label: string; mountingIds: MountingID[] }

export type NativeMountingSystemsType = Record<NativeMountingID, MountingDefinitionType>

export type PartnerAccessoriesType = Record<PartnerAccessoriesID, MountingDefinitionType>

export const MOUNTING_SYSTEMS: Record<
  MountingID,
  { manufacturer: string; types: MountingSystemType[]; availableCountries: string[] }
> = {
  ironridge: { manufacturer: 'IronRidge', types: ['onRoof'], availableCountries: ['US'] },
  sunmodoSMRPitched: { manufacturer: 'Sunmodo', types: ['onRoof'], availableCountries: ['US', 'CA'] },
  sunmodoNanoRack: { manufacturer: 'Sunmodo', types: ['onRoof'], availableCountries: ['US', 'CA'] },
  fastensol: { manufacturer: 'Fastensol', types: ['onRoof'], availableCountries: ['GB'] },
  k2OnRoofUK: { manufacturer: 'K2 Systems', types: ['onRoof'], availableCountries: ['GB'] },
  solarRoofPro: { manufacturer: 'Clenergy', types: ['onRoof'], availableCountries: ['GB'] },
  esdec: { manufacturer: 'ESDEC', types: ['onRoof'], availableCountries: ['GB'] },
  metasole: { manufacturer: 'Renusol', types: ['onRoof'], availableCountries: ['GB'] },
  variosole: { manufacturer: 'Renusol', types: ['onRoof'], availableCountries: ['GB'] },
  renusol: {
    manufacturer: 'Renusol',
    types: ['onRoof', 'flatRoof', 'ground'],
    availableCountries: ['GB', 'PL', 'DE', 'NL', 'BE'],
  },
  schletter: { manufacturer: 'Schletter', types: ['onRoof'], availableCountries: ['GB'] },
  msPitchedProLine: {
    manufacturer: 'Mounting Systems GmbH',
    types: ['onRoof'],
    availableCountries: ['GB', 'PL', 'DE', 'NL', 'ES'],
  },
  msTrapezeProLine: { manufacturer: 'Mounting Systems GmbH', types: ['onRoof'], availableCountries: ['GB'] },
  k2OnRoof: { manufacturer: 'K2 Systems', types: ['onRoof'], availableCountries: ['US'] },
  k2SimpleTilt: { manufacturer: 'K2 Systems', types: ['onRoof'], availableCountries: ['US'] },
  unirac: { manufacturer: 'Unirac', types: ['onRoof'], availableCountries: ['US'] },
  nova: { manufacturer: 'NOVA', types: ['onRoof', 'flatRoof'], availableCountries: ['AU'] },
  solarNestRooftop: { manufacturer: 'SolarNEST', types: ['flatRoof'], availableCountries: ['US'] },
  solarNestGroundMount: { manufacturer: 'SolarNEST', types: ['ground'], availableCountries: ['US'] },
  viridian: { manufacturer: 'Viridian', types: ['inRoof'], availableCountries: ['GB'] },
  gseInRoof: { manufacturer: 'GSE', types: ['inRoof'], availableCountries: ['GB'] },
  flatFix: { manufacturer: 'ESDEC', types: ['flatRoof'], availableCountries: ['GB'] },
  vanDerValk: { manufacturer: 'Valk Solar System', types: ['onRoof', 'flatRoof'], availableCountries: ['GB'] },
  k2GroundMount: { manufacturer: 'K2 Systems', types: ['ground'], availableCountries: ['US'] },
  solarPortGroundMount: { manufacturer: 'Solarport Systems', types: ['ground'], availableCountries: ['GB'] },
  fastRack: { manufacturer: 'Fast-Rack', types: ['onRoof'], availableCountries: ['CA'] },
  snapnrackUltraRail: { manufacturer: 'SnapNRack', types: ['onRoof'], availableCountries: ['US'] },
}

type MountingDefinitionType = {
  class: any // MountingSystem
  manufacturer: string
  getInputs: (projectForm: FormApi, system: StudioSystemType) => NativeMountingInputs
  postProcessMergedItems?: (
    items: any[],
    options: Record<string, unknown>,
    system?: import('types/global').StudioSystemType
  ) => any[]
}

export interface CheckboxGroupInput {
  label: string
  variableName: string
  default: string[]
  options: InputOption[]
  /** Overrides the default MUI legend colour when set (e.g. to match TextField labels). */
  labelColor?: string
  /** Info icon tooltip shown beside the legend label. */
  tooltip?: string
}

export interface ReadOnlyLabel {
  label: string
  value: string
}

export interface NativeMountingInputs {
  dropdownInputs?: DropdownInput[]
  /** Rendered immediately after the anchor type dropdown (when present). */
  textFieldInputsAfterAnchor?: TextFieldInput[]
  /** Read-only labels rendered after the named text field variable. */
  readOnlyLabelsAfterField?: { afterVariableName: string; labels: ReadOnlyLabel[] }
  textFieldInputs?: TextFieldInput[]
  /** Rendered after `textFieldInputs` (e.g. Van der Valk Dlubal fields at the bottom). */
  trailingDropdownInputs?: DropdownInput[]
  trailingTextFieldInputs?: TextFieldInput[]
  checkboxGroupInputs?: CheckboxGroupInput[]
}

export interface MountingFieldCacheConfig {
  dataKey: string
  resultKey: string
  getCacheData: () => { success: boolean; value: any }
  cacheIndicatorTooltip?: string
  changedFromCacheTooltip?: string
}

export interface MountingFieldFetchConfig extends MountingFieldCacheConfig {
  fetchFunction: () => Promise<void>
  timeoutSeconds: number
  isValidMountingSystem?: (mountingName: string) => boolean
  fallbackValue?: any
  timeoutErrorMessage?: string
  /** When set, maps a fetch error to a user-facing message; otherwise timeoutErrorMessage is used. */
  resolveFetchErrorMessage?: (error: unknown) => string | undefined
  /** Additional integration fields populated from cache when Fetch succeeds. */
  companionApplyFields?: Array<{
    variableName: string
    getCacheData: () => { success: boolean; value: unknown }
  }>
}

export interface TextFieldInput {
  label: string
  variableName: string
  default: InputValue
  type: string
  min?: number
  max?: number
  step?: number
  /** Maximum number of decimal places allowed for manual input. Value is rounded on apply. */
  maxDecimals?: number
  button?: {
    label: string
    disabled?: boolean
    fetchConfig?: MountingFieldFetchConfig
  }
}

export interface DropdownInput {
  label: string
  variableName: string
  default: InputValue
  options: InputOption[]
  /** Info icon tooltip shown beside the dropdown label area. */
  tooltip?: string
  button?: {
    label: string
    disabled?: boolean
    fetchConfig?: {
      dataKey: string
      resultKey: string
      fetchFunction: () => Promise<void>
      timeoutSeconds: number
      isValidMountingSystem?: (mountingName: string) => boolean
      fallbackValue?: any
      timeoutErrorMessage?: string
      resolveFetchErrorMessage?: (error: unknown) => string | undefined
      getCacheData: () => { success: boolean; value: any }
      cacheIndicatorTooltip?: string
      changedFromCacheTooltip?: string
      mapResultToValue?: (rawValue: any) => InputValue
    }
  }
}

export type InputOption = {
  label: number | string
  value: InputValue
}

export type InputValue = boolean | number | string | RoofHookCalculationMethodValue
export type RoofHookCalculationMethodValue = 'mcs' | 'basic'

export type RoofHookCalculationMethodOption = {
  label: string
  value: RoofHookCalculationMethodValue
}
